import React, { useEffect, useState } from 'react';
import { useAuth} from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { PERMISSIONS } from '../utils/constants';
import { Settings, Bell, Database, UserCog, Warehouse, User, Activity, Building2, X, ChevronDown, LogOut } from 'lucide-react';

const TopNavBar = ({ onNavigate, currentSelection }) => {
  const { permissions, user, currentSiteId, changeSiteId, userSites, logout } = useAuth();
  const { theme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSiteMenuOpen, setIsSiteMenuOpen] = useState(false);

  // Navigation items with their permissions
  const navItems = [
    {
      name: 'SCADA',
      icon: <Activity className="w-5 h-5" />,
      id: 'scada',
      permission: PERMISSIONS.SCADA.VIEW_SCADA
    },
    {
      name: 'Alarms', 
      icon: <Bell className="w-5 h-5" />,
      id: 'alarms',
      permission: PERMISSIONS.ALARMS.VIEW_ALARMS
    },
    {
      name: 'Assets',
      icon: <Database className="w-5 h-5" />,
      id: 'assets', 
      permission: PERMISSIONS.ASSETS.VIEW_ASSETS
    },
    {
      name: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      id: 'settings'
    }
  ];

  // Check if user has permission for a specific feature
  const hasPermission = (permission) => {
    return permission ? permissions?.includes(permission) : true;
  };

  useEffect(() => {
    // Try to get last location from localStorage for current site
    const lastLocations = JSON.parse(localStorage.getItem('lastNavLocations') || '{}');
    const lastLocation = lastLocations[currentSiteId];
    
    if (lastLocation && hasPermission(navItems.find(item => item.id === lastLocation)?.permission)) {
      onNavigate(lastLocation);
    } else if (navItems.find(item => hasPermission(item.permission))?.id) {
      onNavigate(navItems.find(item => hasPermission(item.permission)).id);
    } else {
      // Default to settings if no valid last location or no permission
      onNavigate('settings');
    }
  }, [currentSiteId]);

  const handleItemClick = (id) => {
    onNavigate(id);
    setIsMenuOpen(false);
    // Store last location per site in localStorage
    const lastLocations = JSON.parse(localStorage.getItem('lastNavLocations') || '{}');
    lastLocations[currentSiteId] = id;
    localStorage.setItem('lastNavLocations', JSON.stringify(lastLocations));
  };

  const getCurrentItem = () => {
    return navItems.find(item => item.id === currentSelection);
  };

  const getCurrentSite = () => {
    return userSites?.find(site => site.site_id === currentSiteId);
  };

  const handleSiteChange = async (siteId) => {
    try {
      await changeSiteId(siteId);
      setIsSiteMenuOpen(false);
    } catch (error) {
      console.error('Failed to change site:', error);
    }
  };

  return (
    <nav className={`w-full px-4 py-1 backdrop-blur-xl bg-opacity-90 fixed top-0 left-0 z-50 ${theme === 'light' ? 'bg-white border-b border-gray-300/50' : 'bg-slate-900/90 border-b border-gray-700/50'}`}>
      <div className="w-full flex items-center justify-between">
        {/* Company Logo and Site Selector */}
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0 relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl blur opacity-0 group-hover:opacity-40 transition duration-300"></div>
            <img 
              src={theme === 'light' ? '/logo-white.png' : '/logo-dark.png'}
              alt="Company Logo" 
              className="hidden md:block h-12 w-auto relative hover:scale-110 transition-transform duration-200"
            />
            <img 
              src={theme === 'light' ? '/logo-square-white.png' : '/logo-square-dark.png'}
              alt="Company Logo" 
              className="md:hidden h-12 w-auto relative hover:scale-110 transition-transform duration-200"
            />
          </div>

          {/* Site Selector Dropdown */}
          <div className="relative">
            {userSites?.length === 1 ? (
              // Single site - just show the name without dropdown
              <div className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-medium
                ${theme === 'light' 
                  ? 'bg-white text-slate-700 border border-slate-200/50' 
                  : 'bg-slate-800/50 text-slate-200 border border-slate-700/50'}`}
              >
                <span className="relative">
                  <span className="relative">{userSites[0].name}</span>
                </span>
              </div>
            ) : (
              // Multiple sites - show dropdown
              <>
                <button
                  onClick={() => setIsSiteMenuOpen(!isSiteMenuOpen)}
                  className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300
                    ${theme === 'light' 
                      ? 'bg-white text-slate-700 hover:bg-slate-50 shadow-sm hover:shadow-md border border-slate-200/50' 
                      : 'bg-slate-800/50 text-slate-200 hover:bg-slate-700/50 shadow-lg shadow-slate-900/20 border border-slate-700/50'}`}
                >
                  <span className="relative">
                    <span className={`absolute -inset-1 rounded-lg ${isSiteMenuOpen ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 blur-sm' : ''}`}></span>
                    <span className="relative">{getCurrentSite()?.name || 'Select Site'}</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 ml-3 transition-transform duration-300 ${isSiteMenuOpen ? 'rotate-180 text-blue-500' : ''}`} />
                </button>

                {isSiteMenuOpen && (
                  <div className={`absolute top-full left-0 mt-2 w-56 py-2 rounded-xl backdrop-blur-xl z-50 transition-all duration-300
                    ${theme === 'light' 
                      ? 'bg-white/90 shadow-xl shadow-slate-200/50 border border-slate-200/50' 
                      : 'bg-slate-800/90 shadow-xl shadow-slate-900/50 border border-slate-700/50'}`}>
                    {userSites?.map((site) => (
                      <button
                        key={site.site_id}
                        onClick={() => handleSiteChange(site.site_id)}
                        className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 relative group
                          ${theme === 'light'
                            ? 'text-slate-700 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50'
                            : 'text-slate-200 hover:bg-gradient-to-r hover:from-slate-700/50 hover:to-slate-600/50'
                          }
                          ${site.site_id === currentSiteId 
                            ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 font-semibold' 
                            : ''}`}
                      >
                        <span className="relative z-10">{site.name}</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 to-blue-500/0 group-hover:from-cyan-500/5 group-hover:to-blue-500/5 transition-all duration-300 rounded-lg"></div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Mobile/Tablet Selected Item & Dropdown */}
        <div className="lg:hidden relative">
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`flex items-center justify-between w-40 px-4 py-2.5 rounded-lg text-sm font-semibold
              bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30
              transition-all duration-300`}
          >
            <span className="flex items-center">
              {getCurrentItem()?.icon}
              <span className="ml-2">{getCurrentItem()?.name}</span>
            </span>
            <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Mobile/Tablet Dropdown Menu */}
          {isMenuOpen && (
            <div className={`absolute top-full right-0 mt-2 w-40 py-2 rounded-lg shadow-xl z-auto
              ${theme === 'light' ? 'bg-white' : 'bg-slate-800'}`}>
              {navItems.map((item, index) => (
                hasPermission(item.permission) && item.id !== currentSelection && (
                  <button
                    key={index}
                    onClick={() => handleItemClick(item.id)}
                    className={`w-full flex items-center px-4 py-3 text-sm font-semibold transition-colors
                      ${theme === 'light'
                        ? 'text-slate-700 hover:bg-slate-100'
                        : 'text-slate-200 hover:bg-slate-700'
                      }`}
                  >
                    <span className="flex items-center">
                      {item.icon}
                      <span className="ml-2">{item.name}</span>
                    </span>
                  </button>
                )
              ))}
            </div>
          )}
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-2 ml-auto">
          {navItems.map((item, index) => (
            hasPermission(item.permission) && (
              <button
                key={index}
                onClick={() => handleItemClick(item.id)}
                className={`flex items-center px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ease-out
                  hover:scale-105 hover:shadow-lg 
                  relative overflow-hidden
                  ${currentSelection === item.id 
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30' 
                    : ''}
                  ${theme === 'light' 
                    ? `${currentSelection !== item.id ? 'text-slate-700 bg-white/50 hover:bg-white hover:shadow-cyan-200/50' : ''}`
                    : `${currentSelection !== item.id ? 'text-slate-200 bg-slate-800/50 hover:bg-slate-800 hover:shadow-cyan-900/50' : ''}`
                  }
                  border border-cyan-200/20 backdrop-blur-sm
                `}
              >
                <span className="relative flex items-center">
                  {item.icon}
                  <span className="ml-2">{item.name}</span>
                </span>
                {currentSelection === item.id && (
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 animate-pulse"></div>
                )}
              </button>
            )
          ))}
          <button
            onClick={logout}
            className={`flex items-center p-2.5 rounded-lg transition-all duration-300 ease-out
              hover:scale-105 hover:shadow-lg ml-2
              ${theme === 'light' 
                ? 'text-slate-700 bg-white/50 hover:bg-white hover:shadow-red-200/50' 
                : 'text-slate-200 bg-slate-800/50 hover:bg-slate-800 hover:shadow-red-900/50'
              }
              border border-red-200/20 backdrop-blur-sm
            `}
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default TopNavBar;